// src/components/about/AchievementsSection.js
import React, { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { FaUsers, FaGraduationCap, FaCalendarAlt, FaAward, FaLaptopCode, FaWhatsapp } from 'react-icons/fa';
import gsap from 'gsap';
import Heading from '../common/Heading';
import Section from '../common/Section';

const AchievementsSection = ({ background = "white" }) => {
  // Set up refs for counter elements
  const traineeCounter = useRef(null);
  const cohortCounter = useRef(null);
  const eventsCounter = useRef(null);
  const communityCounter = useRef(null);
  const employmentCounter = useRef(null);
  
  // Set up intersection observer to trigger animations when section comes into view
  const { ref: sectionRef, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });
  
  // Function to animate a counter from 0 to target value
  const animateCounter = (element, target, duration = 2, delay = 0) => {
    if (!element || !element.current) return;
    
    const start = { value: 0 };
    const obj = { value: 0 };
    
    gsap.timeline({
      delay
    }).to(obj, {
      value: target,
      duration,
      ease: "power2.out",
      onUpdate: function() {
        if (element.current) {
          element.current.textContent = Math.round(obj.value).toLocaleString();
        }
      }
    });
  };
  
  // Start counter animations when section comes into view
  useEffect(() => {
    if (inView) {
      animateCounter(traineeCounter, 1000, 2, 0.2);
      animateCounter(cohortCounter, 13, 1.5, 0.4);
      animateCounter(eventsCounter, 20, 1.5, 0.6);
      animateCounter(communityCounter, 3000, 2.5, 0.8);
      animateCounter(employmentCounter, 85, 2, 1.0);
    }
  }, [inView]);
  
  // Achievement data
  const achievements = [
    {
      id: 1,
      icon: <FaUsers />,
      value: <span ref={traineeCounter}>0</span>,
      label: "Successful Trainees",
      color: "bg-blue-100 text-blue-600",
      description: "Youths trained in digital skills since 2021"
    },
    {
      id: 2,
      icon: <FaGraduationCap />,
      value: <span ref={cohortCounter}>0</span>,
      label: "Completed Cohorts",
      color: "bg-green-100 text-green-600",
      description: "Training cycles completed over 4 years"
    },
    {
      id: 3,
      icon: <FaCalendarAlt />,
      value: <span ref={eventsCounter}>0</span>,
      label: "High-Impact Events",
      color: "bg-purple-100 text-purple-600",
      description: "Career talks, seminars, and field trips"
    },
    {
      id: 4,
      icon: <FaUsers />,
      value: <span ref={communityCounter}>0</span>,
      label: "Youth Community",
      color: "bg-indigo-100 text-indigo-600",
      description: "Strong network of connected youth"
    },
    // {
    //   id: 5,
    //   icon: <FaAward />,
    //   value: <span ref={employmentCounter}>0</span>,
    //   label: "Employment Rate",
    //   suffix: "%",
    //   color: "bg-yellow-100 text-yellow-600",
    //   description: "Of graduates find relevant employment"
    // }
  ];
  
  return (
    <Section background={background} padding="lg">
      <div className="text-center mb-12" ref={sectionRef}>
        <Heading level={2} align="center" className="mb-4" withAccent>
          Our Achievements
        </Heading>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Transforming youth through digital skills since 2021, our impact continues to grow across Sierra Leone
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {achievements.map((achievement, index) => (
          <motion.div
            key={achievement.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true, margin: "-100px" }}
            className="bg-white rounded-lg shadow-md p-6 text-center transform transition-all duration-300 hover:-translate-y-2 hover:shadow-lg"
          >
            <div className="flex justify-center mb-4">
              <div className={`h-16 w-16 rounded-full ${achievement.color} flex items-center justify-center text-2xl`}>
                {achievement.icon}
              </div>
            </div>
            <div className="text-3xl font-bold text-primary-dark mb-2">
              {achievement.value}{achievement.suffix || ''}
            </div>
            <h5 className="font-bold text-gray-800 mb-2">{achievement.label}</h5>
            <p className="text-gray-600 text-sm">{achievement.description}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 text-center">

      {/* WhatsApp Community Button */}
      <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <a 
            href="https://chat.whatsapp.com/JktLtRuNOIlAg7mIRACuM2"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-green-500 text-white font-medium text-lg rounded-md hover:bg-green-600 transition-colors shadow-lg"
          >
            <FaWhatsapp className="mr-3 text-2xl" />
            Join Our WhatsApp Community
          </a>
          <p className="text-gray-600 mt-3">
            Connect with other youths, share ideas, and stay updated on our latest programs
          </p>
        </motion.div>
      </div>
      
      <div className="mt-16 bg-gray-50 rounded-lg shadow-md p-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
            <img 
              src="/images/about/achievements-image.jpg" 
              alt="Leotech Achievement Celebration" 
              className="rounded-lg shadow-md"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://placehold.co/600x400/1a237e/ffffff?text=Leotech+Achievements";
              }}
            />
          </div>
          <div className="md:w-1/2">
            <Heading level={3} className="mb-4">
              Impact Beyond Numbers
            </Heading>
            <p className="text-gray-700 mb-4">
              While we're proud of our quantitative achievements, Leotech's true impact goes beyond numbers.
              We're transforming lives by creating pathways to meaningful employment, entrepreneurship, and
              innovation in Sierra Leone.
            </p>
            <p className="text-gray-700 mb-6">
              Our graduates are working in various sectors, from construction and engineering to IT and
              creative industries, applying their digital skills to solve real-world problems and
              contributing to the country's development.
            </p>
            <div className="bg-primary-dark text-white p-4 rounded-lg">
              <div className="font-bold text-lg mb-2 flex items-center">
                <FaLaptopCode className="mr-2" /> Our Vision for 2030
              </div>
              <p>Train 10,000 youths with marketable digital skills across Sierra Leone</p>
            </div>
          </div>
        </div>
      </div>
      
    </Section>
  );
};

export default AchievementsSection;